import { QueryKey, useQuery } from '@tanstack/react-query';

import { getApplicableAddons } from './services';
import useGetEvent from 'features/common/hooks/useGetEvent';

/**
 * Shared cache key for the applicable-add-ons list. The section (list + reorder) and the dashboard
 * (count badge) both build it from the same inputs so the request is deduplicated and the optimistic
 * reorder writes to the exact key the list reads (Dashboard `components/CLAUDE.md` invariant 4).
 */
export const applicableAddonsKey = (eventId?: number, ticketTypeIdsKey?: string): QueryKey => [
	'applicableAddons',
	eventId,
	ticketTypeIdsKey,
];

export const useApplicableAddonsApi = () => {
	const { event } = useGetEvent();
	const eventId = event?.id;
	const ticketTypeIds = (event?.ticketTypes ?? []).map((tt) => tt.ID);
	const ticketTypeIdsKey = ticketTypeIds.join(',');
	const queryKey = applicableAddonsKey(eventId, ticketTypeIdsKey);

	const { data, isFetching, isError, refetch } = useQuery({
		queryKey,
		queryFn: () => getApplicableAddons(eventId as number, ticketTypeIds),
		enabled: !!eventId && ticketTypeIds.length > 0,
	});

	return {
		applicableAddons: data,
		// Spinner only on the first load (fetching with no data yet); a background refetch keeps the list.
		isLoading: isFetching && !data,
		isError,
		refetch,
		queryKey,
	};
};
